import { showNotification } from '@mantine/notifications'
import dayjs from 'dayjs'
import { T } from 'components/common/T'
import i18n from 'i18next'
import { z } from 'zod'
import { api } from '..'

export const exportDashboardToCSVSchema = z
  .object({
    fromDate: z.string().nullish(),
    toDate: z.string().nullish(),
    indicator: z.string().optional(),
    exportMode: z.enum(['all', 'single']).optional(),
  })
  .refine(
    data => {
      if (data.exportMode === 'single') {
        return !!data.indicator
      }
      return true
    },
    {
      message: i18n.t('dashboard.indicatorRequired'),
      path: ['indicator'],
    },
  )
  .refine(
    data => {
      if (data.fromDate && data.toDate) {
        const from = dayjs(data.fromDate)
        const to = dayjs(data.toDate)
        const diffInMonths = to.diff(from, 'month', true)
        return diffInMonths <= 2
      }
      return true
    },
    {
      message: i18n.t('dashboard.maxDateRangeNote'),
      path: ['toDate'],
    },
  )

export type TExportDashboardToCSVFields = z.infer<
  typeof exportDashboardToCSVSchema
>

export const exportDashboardTOCSV =
  ({
    deviceId,
    dashboardId,
    dashboardName,
  }: {
    deviceId: string
    dashboardId: string
    dashboardName: string
  }) =>
  async (data: TExportDashboardToCSVFields) => {
    return api
      .get(`v1/dashboards/export-csv`, {
        responseType: 'blob',
        params: {
          deviceId,
          dashboardId,
          fromDate: data?.fromDate,
          toDate: data?.toDate,
          indicatorCode: data?.indicator,
          timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      })
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          dashboardName ? `${dashboardName}.csv` : 'Dashboard.csv',
        )
        document.body.appendChild(link)
        link.click()
      })
      .catch(e => {
        showNotification({
          color: 'red',
          message: <T>systemMessages.unexpectedError</T>,
        })
      })
  }
